import React, { useEffect, useState } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { DatePicker, Space, Table, Tooltip } from "antd";
import moment, { Moment } from "moment";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { RootState, useAppDispatch } from "app/store";
import { DropdownInput, message } from "components";
import { DailyStreakModel } from "types/model/customer";
import { customerSelectors, fetchCustomerDetail, fetchDailyStreaks } from "../customerSlice";
import styles from "./DailyStreak.module.scss";

function formatTotalDuration(totalMinutes: number) {
  return `${totalMinutes.toFixed(2)} Mins`;
}

function formatStreakLastUpdated(value: string) {
  const parsed = moment(value, moment.ISO_8601, true);
  if (parsed.isValid()) {
    return parsed.format("YYYY-MM-DD HH:mm:ss");
  }
  return value.replace("T", " ");
}

function formatStreakLastUpdatedWithTimezone(value: string, timeZone?: string | null) {
  const formatted = formatStreakLastUpdated(value);
  if (!timeZone || timeZone.trim() === "") {
    return `${formatted} (UTC)`;
  }
  return formatted;
}

function DailyStreak() {
  const dispatch = useAppDispatch();
  const { customerId } = useParams<{ customerId: string }>();
  const customer = useSelector((state: RootState) => customerSelectors.selectById(state, customerId));
  const [date, setDate] = useState<Moment>(moment());
  const [subUserId, setSubUserId] = useState<string>("");
  const [rows, setRows] = useState<DailyStreakModel[]>([]);
  const [subUserOptions, setSubUserOptions] = useState<any[]>([]);

  useEffect(() => {
    if (!customer) {
      setSubUserOptions([]);
      return;
    }
    if (customer.subUserDetails && customer.subUserDetails.length > 0) {
      setSubUserOptions(
        customer.subUserDetails.map((item) => ({
          label: (
            <>
              {item.name}
              {customer.rootUserName === item.name && <div className={styles.accountHolder}>Account holder</div>}
            </>
          ),
          value: item.id,
        }))
      );
      return;
    }
    setSubUserOptions(
      (customer.subUsers ?? []).map((item) => ({
        label: (
          <>
            {item}
            {customer.rootUserName === item && <div className={styles.accountHolder}>Account holder</div>}
          </>
        ),
        value: item,
      }))
    );
  }, [customer]);

  useEffect(() => {
    if (subUserOptions.length === 0) {
      setSubUserId("");
      return;
    }
    if (!subUserOptions.some((option) => option.value === subUserId)) {
      setSubUserId(subUserOptions[0].value as string);
    }
  }, [subUserId, subUserOptions]);

  useEffect(() => {
    if (customerId) {
      dispatch(fetchCustomerDetail(Number(customerId)))
        .then(unwrapResult)
        .catch(message.error);
    }
  }, [dispatch, customerId]);

  useEffect(() => {
    if (!customerId || !subUserId) {
      setRows([]);
      return;
    }
    dispatch(
      fetchDailyStreaks({
        customerId: Number(customerId),
        subUserId,
        date: date.format("YYYY-MM-DD"),
        timezone: undefined,
      })
    )
      .unwrap()
      .then(setRows)
      .catch(message.error);
  }, [customerId, date, dispatch, subUserId]);

  const columns = [
    { title: "User name", dataIndex: "userName", key: "userName" },
    { title: "Learning Category", dataIndex: "learningCategory", key: "learningCategory" },
    {
      title: "Learning Duration(Mins)",
      dataIndex: "learningDurationMinutes",
      key: "learningDurationMinutes",
      render: (value: number) => value.toFixed(2),
    },
    { title: "Platform", dataIndex: "platform", key: "platform" },
    {
      title: (
        <Space size={4}>
          <span>Streak Last Updated</span>
          <Tooltip title="The timestamps displayed below reflect the user's local time zone. If the user's timezone is not available, times are shown in UTC.">
            <InfoCircleOutlined style={{ color: "#858585" }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: "streakLastUpdated",
      key: "streakLastUpdated",
      render: (value: string, row: DailyStreakModel) => formatStreakLastUpdatedWithTimezone(value, row.timeZone),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <div className={styles.filterItem}>
          <div className={styles.filterLabel}>Date:</div>
          <DatePicker value={date} onChange={(value) => value && setDate(value)} className={styles.datePicker} />
        </div>
        <div className={styles.filterItem}>
          <div className={styles.filterLabel}>Subuser:</div>
          <div className={styles.subUserSelectWrapper}>
            <DropdownInput
              value={subUserId}
              onChange={setSubUserId}
              options={subUserOptions}
              placeholder="Subuser"
              mode="single"
            />
          </div>
        </div>
        <div className={`${styles.filterItem} ${styles.durationItem}`}>
          <div className={styles.filterLabel}>Total Duration:</div>
          <div className={styles.totalDuration}>
            {formatTotalDuration(rows.reduce((sum, row) => sum + row.learningDurationMinutes, 0))}
          </div>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={rows}
        rowKey={(row) => `${row.subUserId}-${row.learningCategory}-${row.streakLastUpdated}`}
        pagination={false}
      />
    </div>
  );
}

export default DailyStreak;
